import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { Params     } from '@angular/router';
import { take, tap  } from 'rxjs/operators';

import { VacationRequest } from '@shared/factories';
import { FormatDatesService, HelperService, NotificationService, OverviewTableService, SessionStorageService, VacationRequestsCountersService, VacationRequestService, WebsocketService } from '@shared/services';
import { TableHeaderEntryModel, TableBodyEntryModel, TableBodyCellNestedEntryModel, TableListIconModel, TableBodyCellEntryModel } from '@shared/models';

@Component({
  selector:     'vacation-requests-table',
  styleUrls: ['./vacation-requests-table.component.sass'],
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  templateUrl: '../../table-entries.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class VacationRequestsTableComponent {
  @Input() rows:         VacationRequest[];
  @Input() paginationId: string;
  @Input() isInternal:   boolean;
  @Input() isCustomer:   boolean;
  @Input() loading:      boolean;

  @Output() headerCallback = new EventEmitter<any>();
  constructor(
    private overviewTableService:            OverviewTableService,
    private vacationRequestService:          VacationRequestService,
    private vacationRequestsCountersService: VacationRequestsCountersService,
    private helperService:                   HelperService,
    private websocketService:                WebsocketService,
    private sessionStorageService:           SessionStorageService,
    private notificationService:             NotificationService,
  ) { }

  headerCallbackHandler(res) {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    if (this.paginationId === 'vr-details') return 'vr-details-wrapper';
    else return 'vr-list-wrapper';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    if      (this.paginationId === 'vr-list')    return this.prepareVacationRequestsListTableHeader();
    else if (this.paginationId === 'archive')    return this.prepareVacationRequestsArchiveTableHeader();
    else if (this.paginationId === 'failed-erp') return this.prepareVacationRequestsListTableHeader();
    else if (this.paginationId === 'vr-details') return this.prepareVacationRequestDetailsTableHeader();
  }

  private prepareVacationRequestsTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter',            class: 'name-cell',          sort_by: ['external_employee_last_name', 'external_employee_first_name'], customerOnly: true                                     },
      { title: 'Mitarbeiter (Pers.Nr.)', class: 'name-cell',          sort_by: ['external_employee_last_name', 'external_employee_first_name'], internalOnly: true                                     },
      { title: 'Urlaubsart',  class: 'type-cell', sort_by: ['leave_type_priority']                                         },
      { title: 'Zeitraum',    class: 'week-cell', sort_by: ['starts_on']                                                   },
      { title: 'Zusatzinfo',  class: 'am-flex-center icons-cell relative', pageOnly: ['dashboard', 'archive'], tooltipHtml: this.getExtraInfoTooltip(),
        sortOptions: [
          { class: 'am-flex-center font-small-icon icon-tick',    sort_by: ['prechecked_by_internal_at', 'created_at'], skip: this.isCustomer },
          { class: 'am-flex-center font-small-icon icon-holiday', sort_by: ['holiday',                   'created_at'], active: true          },
          { class: 'am-flex-center font-small-icon icon-tempton', sort_by: ['creator',                   'created_at'], skip: this.isCustomer }
        ]
      },
      { title: 'Aktion',      class: `action-cell ${this.paginationId === 'failed-erp' ? 'failed-erp' : ''}` }
    ];
  }

  private prepareVacationRequestsListTableHeader(): TableHeaderEntryModel[] {
    let cols = this.prepareVacationRequestsTableHeader();
    cols.splice(1, 0, { title: 'Eingang', class: 'date-cell',   sort_by: ['created_at']                                                                          });
    cols.splice(5, 0, { title: 'Status',  class: 'status-cell', sort_by: [this.isInternal ? 'internal_state_priority' : 'customer_state_priority', 'created_at'] });
    return cols;
  }

  private prepareVacationRequestsArchiveTableHeader(): TableHeaderEntryModel[] {
    let cols = this.prepareVacationRequestsTableHeader();
    cols.splice(1, 0, { title: 'Erstellung',  class: 'date-cell',     sort_by: ['created_at']           });
    cols.splice(2, 0, { title: 'Überprüfung', class: 'reviewed-cell', sort_by: ['internal_reviewed_at'] });
    if (this.isInternal) cols.splice(5, 0, { title: 'Status',  class: 'status-cell', sort_by: ['internal_state_priority', 'created_at'] });
    return cols;
  }

  private prepareVacationRequestDetailsTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Urlaubsart', class: 'type-cell'  },
      { title: 'Zeitraum',   class: 'week-cell'  },
      { title: 'Anzahl',     class: 'total-cell' },
    ];
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if      (this.paginationId === 'vr-list')    return this.prepareVacationRequestsTableBody();
    else if (this.paginationId === 'archive')    return this.prepareVacationRequestsTableBody();
    else if (this.paginationId === 'failed-erp') return this.prepareVacationRequestsTableBody();
    else if (this.paginationId === 'vr-details') return this.prepareVacationRequestDetailsTableBody();
  }

  private getExtraInfoTooltip(): string {
    return `
      <div class="web-tooltip white medium bottom left-sm">
        <span class="tooltip-title">Folgende Parameter sind in dieser Spalte sichtbar:</span>
        <ul class="tooltip-list">
          ${this.isInternal ? '<li><span class="icon-tick"></span><span>Intern geprüft</span></li>' : ''}
          ${this.isInternal ? '<li><span class="icon-tempton"></span><span>Erstellt von SB</span></li>' : ''}
          <li><span class="icon-holiday"></span><span>Feiertag(e)</span></li>
        </ul>
      </div>
    `;
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array<TableBodyEntryModel>(10).fill({
      cells: ([
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ',  class: `date-cell`,          value: ' ', pageOnly: ['vr-list', 'failed-erp'] },
        { xs_label: ' ',  class: 'date-cell',          value: ' ', pageOnly: ['archive']               },
        { xs_label: ' ',  class: 'reviewed-cell',      value: ' ', pageOnly: ['archive']               },
        { xs_label: ' ',  class: 'type-cell',          value: ' ',                                     },
          this.overviewTableService.getWeekCellPlaceholder(),
        { xs_label: null, class: 'icons-cell  xs-hide', xs_icons: new Array(3).fill({})                },
        { xs_label: null, class: 'status-cell xs-hide',            pageOnly: ['vr-list', 'failed-erp', this.isInternal ? 'archive' : 'skip'], children: [{ class: 'status-icon loading-animate' }] },
        { class: `action-cell ${this.paginationId === 'failed-erp' ? 'failed-erp' : ''}`, buttons: [{ class: this.paginationId === 'failed-erp' ? 'blue-thin-border custom-action-cell-button' : 'action-cell-button' },
                                                                                                    { class: 'action-cell-button', skip: this.paginationId !== 'failed-erp' }
        ]}
      ] as TableBodyCellEntryModel[]).filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    });
  }

  private prepareVacationRequestsTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((vr: VacationRequest): TableBodyEntryModel => ({
      read_only: this.isInternal && !vr.belongsToInternalLocations,
      cells: [
        this.overviewTableService.getExternalEmployeeCell(vr.externalEmployee, { statusDot: this.overviewTableService.getStatus(vr)?.color }),
        { xs_label: 'Eingang',     class: 'date-cell',     value: FormatDatesService.formatDate(vr.createdAt,  'dd.MM.yyyy HH:mm'), pageOnly: ['vr-list', 'failed-erp']                                       },
        { xs_label: 'Erstellung',  class: 'date-cell',     value: FormatDatesService.formatDate(vr.createdAt,  'dd.MM.yyyy HH:mm'), pageOnly: ['archive']                                                     },
        { xs_label: 'Überprüfung', class: 'reviewed-cell', value: FormatDatesService.formatDate(vr.releasedAt, 'dd.MM.yyyy HH:mm'), pageOnly: ['archive']                                                     },
        { xs_label: 'Urlaubsart',  class: 'type-cell',     value: vr.leaveTypeMapped                                                                                                                                      },
        this.overviewTableService.getWeekCell(vr.calendarWeek, vr.date),
        { xs_label: null,          class: 'icons-cell  xs-hide', children: [{ class: 'am-flex-center font-icon gap-xxs', children: this.helperService.parceTableListIconsWeb(this.getIcons(vr)) }]                        },
        this.overviewTableService.getStatusCell(vr, { paginationId: this.paginationId, pageOnly: ['vr-list', 'failed-erp', this.isInternal ? 'archive' : 'skip'] }),
        { xs_label: 'Zusatzinfo',  class: 'status-cell xs-show-table', xs_icons: this.getIconsRowMobile(vr) },
        { xs_label: '',            class: `action-cell ${this.paginationId === 'failed-erp' ? 'failed-erp' : ''}`,
          buttons: [
            { class: this.paginationId === 'failed-erp' ? 'blue-thin-border custom-action-cell-button' : 'action-cell-button',
              label: this.getActionButtonName(vr),
              routerLink: ['/time-tracking/vr-details', vr.id],
              routerLinkState: this.getFastNavigationData()
            },
            {
              class: 'action-cell-button',
              label: 'Erledigen',
              skip: this.paginationId !== 'failed-erp' || vr.exportResolved,
              click: () => this.manuallyResolve(vr)
            }
          ]
        }
      ].filter((c: TableBodyCellEntryModel) => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    }));
  }

  private getIcons(vr: VacationRequest): TableListIconModel[] {
    return [
      { show: !!vr.precheckedByInternalAt, icon: 'icon-tick',    color: 'white',         tooltipTexts: ['Intern geprüft'], internalOnly: true  },
      { show: !!vr.holidays?.length,       icon: 'icon-holiday', color: 'orange-border', tooltipTexts: vr.holidays.map(h => h.name)            },
      { show: !!vr.createdByInternal,      icon: 'icon-tempton', color: 'white',         tooltipTexts: ['Erstellt von SB'], internalOnly: true }
    ];
  }

  private getIconsRowMobile(vr: VacationRequest): TableBodyCellNestedEntryModel[] {
    let icons: any = this.getIcons(vr).filter(i => i.show);
    if (icons.length) return this.helperService.parceTableListIconsMobile(icons);
    return [{ value: '-' }];
  }

  private getActionButtonName(vr: VacationRequest): string {
    if (this.isInternal) return vr.readOnlyForInternal ? 'Ansehen' : 'Bearbeiten';
    if (this.isCustomer) return vr.readOnlyForCustomer ? 'Ansehen' : 'Bearbeiten';
  }

  private prepareVacationRequestDetailsTableBody(): TableBodyEntryModel[] {
    if (this.loading) return [{
      cells: [
        { class: 'type-cell'  },
        { class: 'week-cell'  },
        { class: 'total-cell' },
      ]
    }];
    return this.rows.map((vr: VacationRequest): TableBodyEntryModel => ({
      cells: [
        { xs_label: 'Urlaubsart', class: 'type-cell',  value: vr.leaveTypeMapped               },
        { xs_label: 'Zeitraum',   class: 'week-cell',  value: `${vr.calendarWeek}\n${vr.date}` },
        { xs_label: 'Anzahl',     class: 'total-cell', value: `${vr.workingVacationDays} ${vr.workingVacationDays === 1 ? 'Arbeitstag' : 'Arbeitstage'}` },
      ]
    }));
  }

  private getFastNavigationData(): Params {
    return ({
      data: this.rows.map(r => ({
        id: r.id,
        page: this.sessionStorageService.pageNumberValue
      }))
    });
  }

  private manuallyResolve(vr: VacationRequest): void {
    this.notificationService.wait();
    this.vacationRequestService.manuallyResolveWorkingPeriod(vr.id).pipe(
      take(1),
      tap(() => this.sessionStorageService.failedResolved = 'vr')
    ).subscribe(
      res   => this.reloadCounters(),
      error => this.notificationService.alert(error.message || error)
    );
  }

  private reloadCounters(): void {
    if (!this.websocketService.wsOnline()) {
      this.vacationRequestsCountersService.reloadCounters();
    }
  }

}
